import React, { useEffect } from 'react'
import { Button, Callout, Classes, Spinner } from '@blueprintjs/core'
import { get } from 'lodash'
import { LinkButton, FileDrop, Table, FileDownloadLink, IconButton, Dialog } from '@ui'
import { SERVER_DATETIME_FMT } from '@constants'
import { notEmpty } from '@utils'
import moment from 'moment'
import { IFile } from '@api'
import {
  IImportResult,
  IOperationCostImportResult,
  IProjectionImportResult,
} from '@api/import/upload'
import { IHistory } from '@api/import/history'
import css from './importFlow.module.scss'

interface IProps {
  title: string
  children: React.ReactNode
  onClose: () => void
  onComplete?: () => void
  importHook: IImport
  excludeUpdated?: boolean
}
interface IStageProps {
  hook: IImport
  onClose: () => void
  onComplete?: () => void
  excludeUpdated?: boolean
}

export interface IPage {
  hasNextPage: boolean
  endCursor: string
  isPaging: boolean
}

export enum Stages {
  COMPLETE,
  HISTORY,
  TEMPLATE_PROVIDER,
  IMPORTING,
}

export type completeStage = Stages.COMPLETE
export type historyStage = Stages.HISTORY
export type templateProviderStage = Stages.TEMPLATE_PROVIDER
export type importingStage = Stages.IMPORTING

export type StageAction = completeStage | historyStage | templateProviderStage | importingStage

function getStage(action: StageAction) {
  switch (action) {
    case Stages.COMPLETE:
      return Complete
    case Stages.HISTORY:
      return History
    case Stages.TEMPLATE_PROVIDER:
      return TemplateProvider
    case Stages.IMPORTING:
      return Importing
    default:
      return TemplateProvider
  }
}

export interface IImport {
  fetchHistory: () => void
  nextHistory: () => void
  setFile: (file: IFile) => void
  setStage: (stage: StageAction) => void
  upload: () => Promise<IImportResult>
  file: IFile | null
  stage: Stages
  result: IImportResult[]
  history: IHistory
  clear: () => void
}

export default function ImportFlow({
  children,
  onClose,
  onComplete,
  title,
  importHook: hook,
  excludeUpdated,
}: IProps) {
  const Stage = getStage(hook.stage)

  return (
    <Dialog
      isOpen
      style={{ width: 'auto', minWidth: '500px' }}
      usePortal
      title={<div className="text-lg leading-normal font-bold p-2">{title}</div>}
      toolbar={
        <div className="dialog-buttons">
          <IconButton
            iconName="times"
            onClick={() => {
              if (hook.stage !== Stages.IMPORTING) {
                hook.clear()
              }

              onClose()
            }}
            tooltip="close"
            minimal
          />
        </div>
      }
      body={<Stage {...{ hook, onClose, onComplete, children, excludeUpdated }} />}
    />
  )
}

const TemplateProvider: React.FC<React.PropsWithChildren<IStageProps>> = ({
  children,
  hook,
  onClose,
  onComplete,
  excludeUpdated,
}) => (
  <>
    <div className={Classes.DIALOG_BODY}>
      <div className={css.details}>{children}</div>
      <FileDrop
        onDrop={(files) => {
          hook.setFile(files[0])
        }}
        files={[hook.file].filter(notEmpty)}
      />
    </div>
    <div className={`${Classes.DIALOG_FOOTER} f f-between mv2`}>
      <LinkButton onClick={() => hook.setStage(Stages.HISTORY)} text="Import History" />
      <Button
        disabled={!hook.file}
        text="Import"
        onClick={() => {
          hook.upload().then(() => {
            hook.setStage(Stages.COMPLETE)
            onComplete && onComplete()
          })
          hook.setStage(Stages.IMPORTING)
        }}
      />
    </div>
  </>
)

const Importing: React.FC<React.PropsWithChildren<IStageProps>> = ({ hook, onClose }) => (
  <>
    <div className={Classes.DIALOG_BODY}>
      <Callout className="centered">
        <div className="f f-center mv2">
          <Spinner size={20} className="mr2" />
          Importing {get(hook.file, 'name')}...
        </div>
        <p>New data will merge with existing data.</p>
      </Callout>
    </div>
    <div className={`${Classes.DIALOG_FOOTER} f f-end mt2`}>
      <Button
        minimal
        text="Close"
        onClick={() => {
          hook.setStage(Stages.TEMPLATE_PROVIDER)
          onClose()
        }}
      />
    </div>
  </>
)

interface IColumn {
  label: string
  render: (result: IOperationCostImportResult | IProjectionImportResult) => React.ReactNode
}
const columns: IColumn[] = [
  {
    label: 'Date',
    render: (result) =>
      moment(result.createdAt, SERVER_DATETIME_FMT).format('MMM DD, YY hh:mm:ss A'),
  },
  {
    label: 'Total',
    render: (result) => <>{result.totalCount}</>,
  },
  {
    label: 'Created',
    render: (result) => <>{result.createdCount}</>,
  },
  {
    label: 'Updated',
    render: (result) => <>{result.updatedCount}</>,
  },
  {
    label: 'Failed',
    render: (result) => <>{result.failedCount}</>,
  },
  {
    label: 'Imported File',
    render: (result) => <FileDownloadLink url={result.url}>Download</FileDownloadLink>,
  },
  {
    label: 'Status',
    render: (result) => <>{result.status}</>,
  },
]
const historyColumns: IColumn[] = [
  ...columns.slice(0, 5),
  {
    label: 'Imported By',
    render: (result) => result.importedBy,
  },
  ...columns.slice(5, 7),
]

const excludeUpdatedHistoryColumns: IColumn[] = [
  ...historyColumns.slice(0, 3),
  ...historyColumns.slice(4),
]

const Complete: React.FC<React.PropsWithChildren<IStageProps>> = ({ hook }) => (
  <div className={Classes.DIALOG_BODY}>
    <Callout>
      <h3 className="mt1 mb0">Import Complete</h3>
      <p>Below is the summary table.</p>
    </Callout>
    <Table columns={columns} data={hook.result} />
  </div>
)

const History: React.FC<React.PropsWithChildren<IStageProps>> = ({
  hook,
  onClose,
  excludeUpdated,
}) => {
  useEffect(hook.fetchHistory, [])

  return (
    <>
      <div className={Classes.DIALOG_BODY} style={{ maxHeight: 500, overflow: 'auto' }}>
        <div className={css.sticky_table_header}>
          <Table
            columns={excludeUpdated ? excludeUpdatedHistoryColumns : historyColumns}
            data={hook.history.results}
            isLoading={hook.history.isLoading}
            page={{
              ...hook.history.page,
              next: hook.nextHistory,
            }}
          />
        </div>
      </div>
      <div className={`${Classes.DIALOG_FOOTER} f f-end mt2`}>
        <Button minimal text="Back" onClick={() => hook.setStage(Stages.TEMPLATE_PROVIDER)} />
      </div>
    </>
  )
}
